import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { JSX } from 'react';
import { createClient, type ClientOptions, type DiscoveryClient, type GetToken } from './client';

const ClientContext = createContext<DiscoveryClient | null>(null);
/** The provider's connection settings, for clients built beside the main one (search). */
const OptionsContext = createContext<ClientOptions | null>(null);

interface ProviderProps {
  apiUrl: string;
  getToken: GetToken;
  children: ReactNode;
}

export function DiscoveryClientProvider({ apiUrl, getToken, children }: ProviderProps): JSX.Element {
  const client = useMemo(() => createClient({ apiUrl, getToken }), [apiUrl, getToken]);
  const options = useMemo(() => ({ apiUrl, getToken }), [apiUrl, getToken]);
  return (
    <ClientContext.Provider value={client}>
      <OptionsContext.Provider value={options}>{children}</OptionsContext.Provider>
    </ClientContext.Provider>
  );
}

export function useDiscoveryClient(): DiscoveryClient {
  const client = useContext(ClientContext);
  if (!client) {
    throw new Error('useDiscoveryClient must be used inside <DiscoveryClientProvider>');
  }
  return client;
}

/** The client when inside a provider, else null — for components also used outside Discovery. */
export function useOptionalDiscoveryClient(): DiscoveryClient | null {
  return useContext(ClientContext);
}

export function useDiscoveryClientOptions(): ClientOptions {
  const options = useContext(OptionsContext);
  if (!options) {
    throw new Error('useDiscoveryClientOptions must be used inside <DiscoveryClientProvider>');
  }
  return options;
}
